/**
 * The registers and statute databases whose word is taken as authoritative here.
 *
 * A finding from a listed host is official; anything else is recorded and labelled unofficial
 * rather than discarded. The server classifies, normalises and de-duplicates — the client checks
 * only that the field looks like a URL, which is what a form control can check on its own.
 *
 * The error is scoped to this form instance. The prototype's single global slot renders one
 * matter's error on the other's panel.
 */

import { useState } from 'react';
import { SectionCard, Legend } from '~/ui/Surface';
import { Button } from '~/ui/Button';
import { Field, UrlInput, Select } from '~/ui/Field';
import { EmptyState, ValidationMessages } from '~/ui/Feedback';
import { Pill } from '~/ui/Pill';
import { SourceStatusPill } from '../jurisdictions/pills';
import { useSources } from '~/api/queries';
import { useAddSource, useRemoveSource } from '~/api/mutations';
import { useDebouncedValidation } from '~/hooks/useDebouncedValidation';
import { validateSourceDraft } from '~/api/validate';
import { useToast } from '~/hooks/useToast';
import { problemsOf } from '~/api/client';
import { fmtDate, hostOf, lowerLabel } from '~/lib/format';
import { useMatterView } from '../matter/useMatterView';
import { SkeletonStack } from '~/ui/Surface';
import styles from '../matter/Matter.module.css';

const POLICY_LABEL: Record<string, string> = {
  host: 'This host only',
  host_and_subdomains: 'This host and its subdomains',
  host_and_path_prefix: 'This host under this path',
};

export function SourcesTab() {
  const { code, matter, panel } = useMatterView();
  const sources = useSources(code, matter);
  const add = useAddSource(code, matter);
  const remove = useRemoveSource(code, matter);
  const toast = useToast();

  const [url, setUrl] = useState('');
  const [policy, setPolicy] = useState<'host' | 'host_and_subdomains' | 'host_and_path_prefix'>(
    'host_and_subdomains',
  );

  const check = useDebouncedValidation({
    body: { url, policy },
    enabled: url.trim().length > 0,
    validate: (draft, signal) => validateSourceDraft(code, matter, draft, signal),
  });

  const matterLabel = panel.data?.label ?? matter;

  if (sources.isPending) return <SkeletonStack rows={4} height={40} />;

  const list = sources.data?.sources ?? [];

  return (
    <SectionCard
      title="Official sources"
      note="Listing a registry means trusting the registry, not one page of it — so matching is on host rather than on an exact address. A source added here is interpolated into the research prompts and decides whether a finding comes back marked official."
      maxWidth={1034}
    >
      {list.length === 0 ? (
        <EmptyState variant="bare">
          <p>
            No official {lowerLabel(matterLabel)} source is listed for this register. Everything the
            research finds will be shown as an unofficial source, and a research run cannot be
            queued until at least one is listed — a run with nothing to prefer has nothing to
            prefer.
          </p>
        </EmptyState>
      ) : (
        <ul className={styles.sourceList}>
          {list.map((source) => (
            <li key={source.id} className={styles.sourceRow}>
              <SourceStatusPill status="official" />
              {source.url ? (
                <a
                  href={source.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={styles.externalLink}
                >
                  {source.url}
                </a>
              ) : (
                <span>{source.citationText ?? source.physicalLocation}</span>
              )}
              <Pill tone="neutral" size="mini">
                {POLICY_LABEL[source.policy] ?? source.policy}
              </Pill>
              {source.authoritativeFrom ? (
                <span className={styles.adoption}>
                  Authoritative from {fmtDate(source.authoritativeFrom)}
                  {source.authoritativeTo ? ` to ${fmtDate(source.authoritativeTo)}` : ''}
                </span>
              ) : null}
              <span className={styles.spacer} />
              <Button
                size="sm"
                variant="ghost"
                pending={remove.isPending && remove.variables === source.url}
                onClick={() =>
                  source.url &&
                  remove.mutate(source.url, {
                    onSuccess: () =>
                      toast.show('ok', `${hostOf(source.url ?? '')} is no longer trusted here.`),
                    onError: (error) => {
                      for (const problem of problemsOf(error)) toast.show('error', problem);
                    },
                  })
                }
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.addRow}>
        <Field
          label="Add a source"
          hint="An absolute http or https address. The server normalises it before storing and before comparing, so two spellings of one address cannot sit here as two sources."
          grow={2}
        >
          <UrlInput
            value={url}
            onChange={setUrl}
            placeholder="https://registry.example/legislation"
          />
        </Field>
        <Field label="Trust extends to" grow={0}>
          <Select
            value={policy}
            onChange={setPolicy}
            options={[
              { value: 'host_and_subdomains', label: POLICY_LABEL.host_and_subdomains ?? '' },
              { value: 'host', label: POLICY_LABEL.host ?? '' },
              { value: 'host_and_path_prefix', label: POLICY_LABEL.host_and_path_prefix ?? '' },
            ]}
          />
        </Field>
        <Button
          className={styles.addButton}
          variant="primary"
          pending={add.isPending}
          inactive={url.trim().length === 0 || check.errors.length > 0}
          onClick={() =>
            add.mutate(
              { url, policy },
              {
                onSuccess: (written) => {
                  toast.show('ok', `${hostOf(written.added.url ?? url)} is now trusted here.`);
                  for (const warning of written.warnings ?? []) toast.show('ok', warning);
                  setUrl('');
                },
                onError: (error) => {
                  for (const problem of problemsOf(error)) toast.show('error', problem);
                },
              },
            )
          }
        >
          Add
        </Button>
      </div>

      <ValidationMessages
        tone="error"
        title="This cannot be added"
        messages={check.errors}
        pending={check.pending}
      />
      <ValidationMessages tone="warn" messages={check.warnings} />

      <Legend
        aria-label="What the source pills mean"
        items={[
          { key: <SourceStatusPill status="official" />, label: 'Read from a listed host' },
          { key: <SourceStatusPill status="unofficial" />, label: 'Recorded, but not from one' },
        ]}
      />
    </SectionCard>
  );
}
